import { useEffect } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useLocalization } from '@/hooks/useLocalization';
import { ROUTES } from '@/app/router/routes';
import { AppFooter } from '@/components/layout/AppFooter';
import styles from './VerifyEmailPage.module.css';

/**
 * EmailVerifiedPage — success confirmation after email verification.
 *
 * Mirrors Flutter email_verified_page.dart.
 *
 * Shows a success message with a "Click here to continue" link.
 * If the user is logged in, reloads to home.
 * If the user is not logged in, navigates to login.
 *
 * Flutter listens to the browser's popstate here and goes back again, so Back can't return
 * to the verification link. React blocks the Back / Forward navigation instead, keeping the
 * user on this page (only while it is shown; Flutter never removes its listener).
 *
 * Flutter source files:
 *   - presentation/web/auth/verify_email_signup/email_verified_page.dart
 */

export function EmailVerifiedPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();

  // Back-button trap — only history (POP) navigations are blocked
  const blocker = useBlocker(({ historyAction }) => historyAction === 'POP');
  useEffect(() => {
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);

  function handleContinue() {
    if (auth.currentUser) {
      // Reload the page to home route (mirrors Flutter: window.location.href = '/')
      window.location.href = '/';
    } else {
      void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={`${styles.card} ${styles.cardCentered}`}>
          <svg className={styles.successIcon} viewBox="0 0 80 80" fill="none" aria-hidden>
            <circle cx="40" cy="40" r="38" strokeWidth="3" />
            <path d="M24 40L35 51L56 30" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>

          <h1 className={styles.title}>{t('Email Verified!')}</h1>
          <p className={styles.subtitle}>{t('Your email address was successfully verified.')}</p>

          <button type="button" onClick={handleContinue} className={styles.link}>
            {t('Click here to continue')}
          </button>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}
